import React, { useState } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { CalendarMonth, Assessment } from "@mui/icons-material";
import api from "../../services/api";
import { useNavigate } from "react-router-dom";
import { useMyContext } from "../../store/ContextApi";

const WashSummaryReport = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { isAdmin } = useMyContext();

  const years = Array.from(
    { length: 2 },
    (_, i) => new Date().getFullYear() - i
  );
  const monthNames = [
    { name: "Jan", value: 1 },
    { name: "Feb", value: 2 },
    { name: "Mar", value: 3 },
    { name: "Apr", value: 4 },
    { name: "May", value: 5 },
    { name: "Jun", value: 6 },
    { name: "Jul", value: 7 },
    { name: "Aug", value: 8 },
    { name: "Sep", value: 9 },
    { name: "Oct", value: 10 },
    { name: "Nov", value: 11 },
    { name: "Dec", value: 12 },
  ];

  const fetchReport = async () => {
    setLoading(true);
    setError("");
    setReport(null);
    try {
      const response = await api.get(`/report/wash-summary/${year}/${month}`);
      setReport(response.data);
    } catch (err) {
      setError("Failed to fetch report");
    } finally {
      setLoading(false);
    }
  };

  const renderPartnerEarnings = (partners) => (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <b>Name</b>
            </TableCell>
            <TableCell>
              <b>Phone</b>
            </TableCell>
            <TableCell>
              <b>Car Washes</b>
            </TableCell>
            <TableCell>
              <b>Bike Washes</b>
            </TableCell>
            <TableCell>
              <b>Total Earnings (₹)</b>
            </TableCell>
            <TableCell>
              <b>Action</b>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {partners.map((p, index) => (
            <TableRow key={index}>
              <TableCell>{p.partnerName}</TableCell>
              <TableCell>{p.partnerPhone}</TableCell>
              <TableCell>{p.completedCarWashBookings}</TableCell>
              <TableCell>{p.completedBikeWashBookings}</TableCell>
              <TableCell>{Math.round(p.partnerEarning)}</TableCell>
              <TableCell>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    navigate(`/wash-by-partner/${p.partnerId}/${year}/${month}`)
                  }
                >
                  View More
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto", p: 3 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        <Assessment sx={{ verticalAlign: "middle", mr: 1 }} />
        Wash Summary Report
      </Typography>

      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <FormControl sx={{ minWidth: 90 }}>
          <InputLabel>Year</InputLabel>
          <Select
            value={year}
            label="Year"
            onChange={(e) => setYear(e.target.value)}
            size="small"
          >
            {years.map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 100 }}>
          <InputLabel>Month</InputLabel>
          <Select
            value={month}
            label="Month"
            onChange={(e) => setMonth(e.target.value)}
            size="small"
          >
            {monthNames.map((m) => (
              <MenuItem key={m.value} value={m.value}>
                {m.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          sx={{ minWidth: 100 }}
          variant="contained"
          onClick={fetchReport}
          startIcon={<CalendarMonth />}
        >
          Report
        </Button>
      </Box>

      {loading && (
        <Box sx={{ textAlign: "center", mt: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {report && (
        <Box>
          <Typography variant="h6" sx={{ mt: 2 }}>
            Summary for {monthNames.find((m) => m.value === month)?.name} {year}
          </Typography>

          <Typography sx={{ mt: 1 }}>
            Total Completed Car Washes: <b>{report.completedCarWashBookings}</b>
          </Typography>
          <Typography>
            Total Completed Bike Washes:{" "}
            <b>{report.completedBikeWashBookings}</b>
          </Typography>
          <>
            {isAdmin && (
              <>
                {" "}
                <Typography>
                  Total Car Wash Amount: ₹
                  <b>{Math.round(report.completedCarWashAmount)}</b>
                </Typography>
                <Typography>
                  Total Bike Wash Amount: ₹
                  <b>{Math.round(report.completedBikeWashAmount)}</b>
                </Typography>
                <Typography>
                  Total Wash Amount: ₹
                  <b>{Math.round(report.totalWashAmount)}</b>
                </Typography>
              </>
            )}
          </>
          <Typography>
            Total Partner Earnings: ₹
            <b>{Math.round(report.totalPartnerEarnings)}</b>
          </Typography>

          {renderPartnerEarnings(report.partnerEarnings)}
        </Box>
      )}
    </Box>
  );
};

export default WashSummaryReport;
